import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Testimonials from '../components/Testimonials';

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = React.useState('all');

  const categories = ['All', 'Web Development', 'Mobile Apps', 'E-commerce', 'Enterprise'];

  const projects = [
    {
      id: 1,
      title: 'TechVista E-commerce Platform',
      category: 'E-commerce',
      description: 'A scalable e-commerce solution with advanced inventory management and real-time analytics',
      image: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=800&q=80',
      tags: ['React', 'Node.js', 'MongoDB', 'Stripe'],
      link: '#'
    },
    {
      id: 2,
      title: 'HealthConnect Pro',
      category: 'Web Development',
      description: 'Healthcare management system with telemedicine capabilities and patient portal',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
      tags: ['Next.js', 'PostgreSQL', 'WebRTC'],
      link: '#'
    },
    {
      id: 3,
      title: 'EduLearn Platform',
      category: 'Web Development',
      description: 'Interactive LMS with AI-powered recommendations and collaboration tools',
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80',
      tags: ['React', 'Firebase', 'OpenAI'],
      link: '#'
    },
    {
      id: 4,
      title: 'FinanceFlow CRM',
      category: 'Enterprise',
      description: 'CRM for financial advisors with portfolio tracking and automated reporting',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      tags: ['React', 'Express', 'MySQL'],
      link: '#'
    },
    {
      id: 5,
      title: 'FitTrack Mobile App',
      category: 'Mobile Apps',
      description: 'Fitness tracking app with workout plans and nutrition guidance',
      image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80',
      tags: ['React Native', 'Firebase'],
      link: '#'
    },
    {
      id: 6,
      title: 'RetailHub POS System',
      category: 'Enterprise',
      description: 'Complete POS solution for retail businesses with inventory management',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
      tags: ['React', 'Node.js', 'PostgreSQL'],
      link: '#'
    },
    {
      id: 7,
      title: 'Acyutah Technologies Corporate Website',
      category: 'Web Development',
      description: 'Modern corporate website showcasing enterprise software solutions (FineDocs & FineFlow), IT consulting services, and digital transformation.',
      image: '/images/acyutah_website_homepage.png',
      tags: ['WordPress', 'PHP', 'MySQL', 'Custom Theme'],
      link: 'https://www.acyutah.com/'
    },
    {
      id: 8,
      title: 'ICMR - NICPR Medical Research Website',
      category: 'Web Development',
      description: 'Official medical research website for ICMR-NICPR featuring cancer screening resources, research hubs, and public awareness frameworks.',
      image: '/images/nicpr_website_mockup.png',
      tags: ['React', 'Node.js', 'PostgreSQL', 'TailwindCSS'],
      link: 'https://nicpr.org/'
    },
    {
      id: 9,
      title: 'Zubelin Paris — Luxury Artificial & Fashion Jewellery',
      category: 'E-commerce',
      description: 'High-converting D2C e-commerce platform for handcrafted artificial jewellery, German silver & oxidised collections, 1-GM gold sets, and Razorpay checkout.',
      image: '/images/zubelin_live_hero.png',
      tags: ['React SPA', 'TailwindCSS', 'Razorpay', 'Cloudflare CDN'],
      link: 'https://zubelin.com/'
    }
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category.toLowerCase() === activeFilter.toLowerCase());

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-cyan-400 text-sm font-medium uppercase tracking-wider">Featured Projects & Case Studies</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our <span className="bg-gradient-to-r from-cyan-400 via-amber-400 to-blue-500 bg-clip-text text-transparent">Projects & Solutions</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Explore our successful client engineering projects, web applications, and enterprise software solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-12 bg-gray-950">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category.toLowerCase())}
                className={`px-6 py-2 rounded-lg transition-all duration-300 ${activeFilter === category.toLowerCase()
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="group bg-gradient-to-br from-gray-800/50 to-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl overflow-hidden hover:border-cyan-500 transition-all duration-300 hover:scale-105"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-cyan-400 text-sm mb-2">{project.category}</div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-1 text-sm text-gray-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.link}
                    target={project.link.startsWith('http') ? '_blank' : '_self'}
                    rel={project.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors group/btn"
                  >
                    <span className="text-sm font-semibold">{project.link.startsWith('http') ? 'Visit Live Website' : 'View Case Study'}</span>
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
            <p className="text-gray-400 text-lg mb-8">Let's create something amazing together</p>
            <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-lg">
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
